// src/components/ui/SelectField.jsx
import React, { useMemo, useState } from "react";
import Select from "react-select";

const SelectField = ({
  label,
  name,
  value,
  onChange,
  options = [],
  placeholder = "Seleccionar...",
  displayFn,
  valueKey = "_id",
  isMulti = false,
  disabled = false,
  required = false,
  className = "",
  searchMode = "default",
}) => {
  const [inputValue, setInputValue] = useState("");
  const isMobile = useMemo(() => {
    if (typeof window === "undefined") return false;
    return window.innerWidth <= 768;
  }, []);

  const selectOptions = options.map((opt) => ({
    value: opt[valueKey],
    label: displayFn ? displayFn(opt) : opt.name || opt.label,
    raw: opt,
  }));

  const selectedValue = isMulti
    ? selectOptions.filter((opt) =>
        Array.isArray(value) ? value.includes(opt.value) : false,
      )
    : selectOptions.find((opt) => opt.value === value) || null;

  const filteredOptions = useMemo(() => {
    if (searchMode !== "examCodeFirst") return selectOptions;

    const term = String(inputValue || "")
      .trim()
      .toLowerCase();

    if (!term) return selectOptions;

    const getCode = (opt) =>
      String(opt?.raw?.examen_cod ?? opt?.value ?? "")
        .trim()
        .toLowerCase();
    const getName = (opt) =>
      String(opt?.raw?.nombre ?? "")
        .trim()
        .toLowerCase();

    const exactCodeMatches = selectOptions.filter((opt) => getCode(opt) === term);

    if (exactCodeMatches.length > 0) {
      const remainingMatches = selectOptions.filter((opt) => {
        if (getCode(opt) === term) return false;
        return (
          getCode(opt).includes(term) ||
          getName(opt).includes(term) ||
          String(opt.label).toLowerCase().includes(term)
        );
      });

      return [...exactCodeMatches, ...remainingMatches];
    }

    const codeMatches = selectOptions.filter((opt) => getCode(opt).includes(term));
    if (codeMatches.length > 0) return codeMatches;

    return selectOptions.filter(
      (opt) =>
        getName(opt).includes(term) ||
        String(opt.label).toLowerCase().includes(term),
    );
  }, [inputValue, searchMode, selectOptions]);

  return (
    <div>
      {label && (
        <label className="block text-sm font-medium mb-1">{label}</label>
      )}

      {/* 📱 MÓVIL */}
      {isMobile && !isMulti ? (
        <select
          name={name}
          value={value || ""}
          disabled={disabled}
          required={required}
          onChange={(e) => {
            const selected = options.find(
              (opt) => String(opt[valueKey]) === e.target.value,
            );

            onChange({
              target: {
                name,
                value: e.target.value,
                raw: selected || null,
              },
            });
          }}
          className={`w-full border border-gray-300 rounded-md px-3 py-2 bg-white ${className}`}
        >
          <option value="">{placeholder}</option>
          {options.map((opt) => (
            <option key={opt[valueKey]} value={opt[valueKey]}>
              {displayFn ? displayFn(opt) : opt.name || opt.label}
            </option>
          ))}
        </select>
      ) : (
        /* 💻 DESKTOP o MULTI → react-select */
        <Select
          options={filteredOptions}
          value={selectedValue}
          isMulti={isMulti}
          isDisabled={disabled}
          placeholder={placeholder}
          isClearable
          isSearchable
          inputValue={inputValue}
          onInputChange={(newValue, actionMeta) => {
            if (actionMeta.action === "input-change") {
              setInputValue(newValue);
            }
            if (
              actionMeta.action === "menu-close" ||
              actionMeta.action === "input-blur"
            ) {
              setInputValue("");
            }
          }}
          classNamePrefix="react-select"
          menuPortalTarget={document.body}
          menuPosition="fixed"
          styles={{
            menuPortal: (base) => ({ ...base, zIndex: 9999 }),
          }}
          onChange={(selected) => {
            if (isMulti) {
              onChange({
                target: {
                  name,
                  value: selected ? selected.map((s) => s.raw) : [],
                },
              });
            } else {
              onChange({
                target: {
                  name,
                  value: selected ? selected.value : "",
                  raw: selected?.raw || null,
                },
              });
            }
          }}
        />
      )}
    </div>
  );
};

export default SelectField;
